'use client'

import Image from 'next/image'
import { useEffect, useState } from 'react'

// Right-hand showcase panel for the auth split layout: a rounded luminist
// painting card that rotates through three product promises. Grain is applied
// at render time via an SVG feTurbulence overlay (see NOISE) so the CDN
// assets stay clean — tune the texture here, not in the images.
const SLIDES = [
  {
    src: 'https://cdn.soku.ai/marketing/auth/slide-data-v1.jpg',
    title: 'Chat with Your Data',
    description:
      'Don’t dig through dashboards. Just ask Soku to find your best-performing creatives or audiences.',
  },
  {
    src: 'https://cdn.soku.ai/marketing/auth/slide-optimize-v1.jpg',
    title: 'Proactive Optimization',
    description:
      'Soku doesn’t just report; it suggests specific actions to improve your ROAS and lower CPA.',
  },
  {
    src: 'https://cdn.soku.ai/marketing/auth/slide-watch-v1.jpg',
    title: 'Always on Watch',
    description:
      'Soku watches every account around the clock — and only asks you when the call is yours to make.',
  },
]

const SLIDE_INTERVAL_MS = 6000

const NOISE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"

export function AuthShowcase({
  override,
}: {
  /**
   * One caller-supplied message, replacing the rotating Soku slides.
   *
   * When present the panel becomes a single static card: the caller supplies
   * one message, and rotating it against itself would just look like a bug.
   * The grain overlay and readability scrim stay, so the panel keeps its
   * shape.
   */
  override?: {
    title: string
    description: string
    imageUrl: string | null
  }
} = {}) {
  const [active, setActive] = useState(0)

  useEffect(() => {
    // No rotation with a single static card — and the timer must not run, or it
    // would keep re-rendering a panel that never changes.
    if (override) return
    const timer = setInterval(
      () => setActive(current => (current + 1) % SLIDES.length),
      SLIDE_INTERVAL_MS,
    )
    return () => clearInterval(timer)
  }, [override])

  const slides = override
    ? [
        {
          // Falls back to Soku's first slide image when the caller supplies
          // none — a partner that uploaded nothing, or a first-party screen
          // that wants the product image under its own copy. An empty panel
          // reads as broken; a neutral image with the caller's copy over it
          // reads as intentional.
          src: override.imageUrl || SLIDES[0].src,
          title: override.title,
          description: override.description,
        },
      ]
    : SLIDES
  const current = slides[Math.min(active, slides.length - 1)]

  return (
    <div className="relative h-full w-full overflow-hidden rounded-3xl bg-[#1c2740]">
      {slides.map((slide, i) => (
        <div
          key={slide.src}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === active ? 'opacity-100' : 'opacity-0'
          }`}
          aria-hidden={i !== active}
        >
          <Image
            src={slide.src}
            alt=""
            fill
            sizes="50vw"
            className="object-cover"
          />
        </div>
      ))}

      {/* Film grain, rendered live so assets stay clean */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-45 mix-blend-overlay"
        style={{
          backgroundImage: `url("${NOISE}")`,
          // Scale the 240px noise tile up so each speck is ~2 CSS px —
          // 1px specks vanish on retina at normal viewing distance.
          backgroundSize: '480px 480px',
        }}
      />

      {/* Caption + readability scrim */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 via-black/20 to-transparent px-8 pt-24 pb-8 text-white">
        <h3 className="font-serif text-4xl tracking-tight">{current.title}</h3>
        <p className="mt-2.5 max-w-md text-base leading-7 text-white/85">
          {current.description}
        </p>
        {slides.length > 1 && (
          <div className="mt-5 flex gap-1.5">
            {slides.map((slide, i) => (
              <button
                key={slide.src}
                type="button"
                aria-label={`Show slide ${i + 1}`}
                onClick={() => setActive(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === active ? 'w-5 bg-white' : 'w-1.5 bg-white/45'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
